import React, { useRef, useState, MouseEvent, useEffect } from 'react';
import { UploadedImage } from '../types';
import { editImageWithNanoBanana } from '../services/geminiService';

interface ImageEditorProps {
  image: UploadedImage;
  onClose: () => void;
  onSave: (newBase64: string) => void;
}

type Box = { x: number; y: number; width: number; height: number };
type Mode = 'crop' | 'erase';
type Handle = 'tl' | 'tr' | 'bl' | 'br' | 'body';

const TARGET_WIDTH = 1200;
const TARGET_HEIGHT = 675;
const TARGET_ASPECT_RATIO = TARGET_WIDTH / TARGET_HEIGHT;

// Icons
const Spinner = () => (
  <svg className="animate-spin h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

const ImageEditor: React.FC<ImageEditorProps> = ({ image, onClose, onSave }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  
  const [historyStack, setHistoryStack] = useState<string[]>([image.base64]);
  const currentImage = historyStack[historyStack.length - 1];

  const [mode, setMode] = useState<Mode>('erase');
  
  // Crop state
  const [cropBox, setCropBox] = useState<Box & { visible: boolean }>({ x: 0, y: 0, width: 0, height: 0, visible: false });
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
  const [interaction, setInteraction] = useState<{ type: 'move' | 'resize'; handle: Handle; startX: number; startY: number; startBox: Box } | null>(null);

  // Erase state
  const [selectionBoxes, setSelectionBoxes] = useState<Box[]>([]);
  const [drawingBox, setDrawingBox] = useState<Box | null>(null);
  const [selectionStage, setSelectionStage] = useState<'idle' | 'selecting'>('idle');
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);

  // Common state
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      if (imageRef.current && containerRef.current) {
          const { left: cLeft, top: cTop } = containerRef.current.getBoundingClientRect();
          const { left: iLeft, top: iTop, width: displayWidth, height: displayHeight } = imageRef.current.getBoundingClientRect();
          setImageOffset({ x: iLeft - cLeft, y: iTop - cTop });

          const imageAspectRatio = displayWidth / displayHeight;

          let initialWidth, initialHeight, initialX, initialY;

          if (imageAspectRatio > TARGET_ASPECT_RATIO) {
            initialHeight = displayHeight;
            initialWidth = initialHeight * TARGET_ASPECT_RATIO;
            initialX = (displayWidth - initialWidth) / 2;
            initialY = 0;
          } else {
            initialWidth = displayWidth;
            initialHeight = initialWidth / TARGET_ASPECT_RATIO;
            initialX = 0;
            initialY = (displayHeight - initialHeight) / 2;
          }
          
          setCropBox({ x: initialX, y: initialY, width: initialWidth, height: initialHeight, visible: true });
      }
    };
    img.src = `data:image/png;base64,${currentImage}`;
  }, [currentImage]);
  
  const handleMouseDown = (e: MouseEvent, handle: Handle) => {
    if (mode !== 'crop') return;
    e.preventDefault();
    e.stopPropagation();
    
    setInteraction({
        type: handle === 'body' ? 'move' : 'resize',
        handle,
        startX: e.clientX,
        startY: e.clientY,
        startBox: { ...cropBox }
    });
  };

  useEffect(() => {
    const handleMouseMove = (e: globalThis.MouseEvent) => {
        if (!interaction || !imageRef.current) return;
        
        const { left: iLeft, top: iTop, width: imageWidth, height: imageHeight } = imageRef.current.getBoundingClientRect();
        
        const dx = e.clientX - interaction.startX;
        const dy = e.clientY - interaction.startY;
        
        let newBox = { ...interaction.startBox };

        if (interaction.type === 'move') {
            newBox.x = interaction.startBox.x + dx;
            newBox.y = interaction.startBox.y + dy;
            
            newBox.x = Math.max(0, Math.min(newBox.x, imageWidth - newBox.width));
            newBox.y = Math.max(0, Math.min(newBox.y, imageHeight - newBox.height));

        } else {
            const { handle, startBox } = interaction;
            const mouseX = e.clientX - iLeft;
            const mouseY = e.clientY - iTop;

            let newX = startBox.x;
            let newY = startBox.y;
            let newWidth = startBox.width;
            let newHeight = startBox.height;

            const minSize = 20;

            if (handle === 'br') {
                const fixedX = startBox.x;
                const fixedY = startBox.y;
                let tempWidth = mouseX - fixedX;
                if (tempWidth < minSize) tempWidth = minSize;
                newWidth = tempWidth;
                newHeight = newWidth / TARGET_ASPECT_RATIO;
                if(fixedX + newWidth > imageWidth) {
                    newWidth = imageWidth - fixedX;
                    newHeight = newWidth / TARGET_ASPECT_RATIO;
                }
                if(fixedY + newHeight > imageHeight) {
                    newHeight = imageHeight - fixedY;
                    newWidth = newHeight * TARGET_ASPECT_RATIO;
                }
            } else if (handle === 'tl') {
                const fixedX = startBox.x + startBox.width;
                const fixedY = startBox.y + startBox.height;
                let tempWidth = fixedX - mouseX;
                if (tempWidth < minSize) tempWidth = minSize;
                newWidth = tempWidth;
                newHeight = newWidth / TARGET_ASPECT_RATIO;
                if(fixedX - newWidth < 0) {
                    newWidth = fixedX;
                    newHeight = newWidth / TARGET_ASPECT_RATIO;
                }
                if(fixedY - newHeight < 0) {
                    newHeight = fixedY;
                    newWidth = newHeight * TARGET_ASPECT_RATIO;
                }
                newX = fixedX - newWidth;
                newY = fixedY - newHeight;
            } else if (handle === 'tr') {
                const fixedX = startBox.x;
                const fixedY = startBox.y + startBox.height;
                let tempWidth = mouseX - fixedX;
                if (tempWidth < minSize) tempWidth = minSize;
                newWidth = tempWidth;
                newHeight = newWidth / TARGET_ASPECT_RATIO;
                if(fixedX + newWidth > imageWidth) {
                    newWidth = imageWidth - fixedX;
                    newHeight = newWidth / TARGET_ASPECT_RATIO;
                }
                if(fixedY - newHeight < 0) {
                    newHeight = fixedY;
                    newWidth = newHeight * TARGET_ASPECT_RATIO;
                }
                newY = fixedY - newHeight;
            } else if (handle === 'bl') {
                const fixedX = startBox.x + startBox.width;
                const fixedY = startBox.y;
                let tempWidth = fixedX - mouseX;
                if (tempWidth < minSize) tempWidth = minSize;
                newWidth = tempWidth;
                newHeight = newWidth / TARGET_ASPECT_RATIO;
                if(fixedX - newWidth < 0) {
                    newWidth = fixedX;
                    newHeight = newWidth / TARGET_ASPECT_RATIO;
                }
                if(fixedY + newHeight > imageHeight) {
                    newHeight = imageHeight - fixedY;
                    newWidth = newHeight * TARGET_ASPECT_RATIO;
                }
                newX = fixedX - newWidth;
            }
            newBox = { x: newX, y: newY, width: newWidth, height: newHeight };
        }
        setCropBox({ ...newBox, visible: true });
    };

    const handleMouseUp = () => {
        setInteraction(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [interaction]);

  const handleModeChange = (newMode: Mode) => {
    setMode(newMode);
    setError(null);
    setSelectionBoxes([]);
    setDrawingBox(null);
    setSelectionStage('idle');
    setStartPoint(null);
  }
  
  const performCrop = () => {
    if (!cropBox.visible) {
        setError("자를 영역을 찾을 수 없습니다.");
        return;
    }
    setError(null);
    const img = new Image();
    img.onload = () => {
        const { naturalWidth } = img;
        if (!imageRef.current) return;
        const { width: displayWidth } = imageRef.current.getBoundingClientRect();
        const scale = naturalWidth / displayWidth;
        const sX = cropBox.x * scale;
        const sY = cropBox.y * scale;
        const sWidth = cropBox.width * scale;
        const sHeight = cropBox.height * scale;
        const canvas = document.createElement('canvas');
        canvas.width = TARGET_WIDTH;
        canvas.height = TARGET_HEIGHT;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(img, sX, sY, sWidth, sHeight, 0, 0, TARGET_WIDTH, TARGET_HEIGHT);
        onSave(canvas.toDataURL('image/png').split(',')[1]);
  };
    img.crossOrigin = "anonymous";
    img.src = `data:image/png;base64,${currentImage}`;
  };
  
  const getRelativeCoords = (e: MouseEvent<HTMLDivElement>) => {
    if (!imageRef.current) return { x: 0, y: 0 };
    const { left, top } = imageRef.current.getBoundingClientRect();
    return { x: e.clientX - left, y: e.clientY - top };
  };
  
  const handleClickErase = (e: MouseEvent<HTMLDivElement>) => {
    const currentPoint = getRelativeCoords(e);
    const clickedBoxIndex = selectionBoxes.findIndex(box => 
      currentPoint.x >= box.x && currentPoint.x <= box.x + box.width &&
      currentPoint.y >= box.y && currentPoint.y <= box.y + box.height
    );

    if (clickedBoxIndex > -1) {
      setSelectionBoxes(prev => prev.filter((_, index) => index !== clickedBoxIndex));
      if (selectionStage === 'selecting') {
        setSelectionStage('idle');
        setStartPoint(null);
        setDrawingBox(null);
      }
      return;
    }

    if (selectionStage === 'idle') {
      setSelectionStage('selecting');
      setStartPoint(currentPoint);
    } else {
      setSelectionStage('idle');
      setStartPoint(null);
      setDrawingBox(null);
      if (drawingBox && drawingBox.width > 5 && drawingBox.height > 5) {
        setSelectionBoxes(prev => [...prev, drawingBox]);
      }
    }
  };

  const handleMouseMoveErase = (e: MouseEvent<HTMLDivElement>) => {
    if (selectionStage !== 'selecting' || !startPoint) return;
    const endPoint = getRelativeCoords(e);
    setDrawingBox({
      x: Math.min(startPoint.x, endPoint.x),
      y: Math.min(startPoint.y, endPoint.y),
      width: Math.abs(startPoint.x - endPoint.x),
      height: Math.abs(startPoint.y - endPoint.y),
    });
  };
  
  const handleMouseLeaveErase = () => {
    if (selectionStage === 'selecting') {
      setSelectionStage('idle');
      setStartPoint(null);
      setDrawingBox(null);
    }
  };

  const runEraseProcess = async (prompt: string, imageToProcess: string, maskBoxes?: Box[]) => {
    setIsProcessing(true);
    setError(null);

    const img = new Image();
    img.onload = async () => {
        try {
            let imageDataArray = [];
            const originalImageData = { base64: imageToProcess, mimeType: 'image/png' };
            
            if (maskBoxes && maskBoxes.length > 0) {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    setError("Canvas context를 가져올 수 없습니다.");
                    setIsProcessing(false);
                    return;
                }
                ctx.drawImage(img, 0, 0);

                const { width: displayWidth, height: displayHeight } = imageRef.current!;
                const scaleX = img.naturalWidth / displayWidth;
                const scaleY = img.naturalHeight / displayHeight;

                // Paint white boxes on a copy to use as a mask
                ctx.fillStyle = 'white';
                maskBoxes.forEach(box => {
                    ctx.fillRect(box.x * scaleX, box.y * scaleY, box.width * scaleX, box.height * scaleY);
                });
                const maskedDataUrl = canvas.toDataURL('image/png');
                const [, maskedBase64] = maskedDataUrl.split(',');
                const maskedImageData = { base64: maskedBase64, mimeType: 'image/png' };
                
                // CRITICAL: Send both original and masked images for high-accuracy inpainting.
                imageDataArray = [originalImageData, maskedImageData];
                // Update instruction to specifically link the two images using English JSON format.
                prompt = JSON.stringify({
                  "action": "erase_from_mask",
                  "instructions": "Image 1 is original. Image 2 is white mask. Remove objects from Image 1 that correspond to WHITE areas in Image 2. Fill these areas seamlessly with background.",
                  "output_format": "IMAGE_ONLY"
                });
            } else {
                imageDataArray = [originalImageData];
            }
            
            const resultBase64 = await editImageWithNanoBanana(imageDataArray, prompt);
            setHistoryStack(prev => [...prev, resultBase64]);
            setSelectionBoxes([]);
        } catch (err) {
            setError(err instanceof Error ? err.message : "이미지 수정 중 오류 발생");
        } finally {
            setIsProcessing(false);
        }
    };
    img.crossOrigin = "anonymous";
    img.src = `data:image/png;base64,${imageToProcess}`;
  };
  
  const handleEraseAllText = () => {
    const prompt = JSON.stringify({
      "action": "remove_text_and_logos",
      "description": "Remove all text, numbers, brand logos, and watermarks from the image. Fill with matching background.",
      "output_format": "IMAGE_ONLY"
    });
    runEraseProcess(prompt, currentImage);
  };
  
  const handleEraseSelected = () => {
    if (selectionBoxes.length === 0) return;
    const prompt = JSON.stringify({
      "action": "inpaint_masked_areas",
      "description": "Fill the white masked regions seamlessly with the surrounding background to erase objects.",
      "output_format": "IMAGE_ONLY"
    });
    runEraseProcess(prompt, currentImage, selectionBoxes);
  };

  const handleUndo = () => {
    if (historyStack.length > 1) {
      setHistoryStack(prev => prev.slice(0, -1));
      setSelectionBoxes([]);
      setDrawingBox(null);
      setSelectionStage('idle');
      setError(null);
    }
  };
  
  const combinedMouseEvents = mode === 'erase' ? {
      onClick: handleClickErase,
      onMouseMove: handleMouseMoveErase,
      onMouseLeave: handleMouseLeaveErase
  } : {};

  return (
    <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm z-50 flex flex-col items-center justify-center p-4 animate-fade-in overflow-auto">
        <div className="text-center mb-4 shrink-0">
            <h2 className="text-3xl font-bold text-white">이미지 상세 수정</h2>
            <div className="flex items-center justify-center gap-2 rounded-lg p-1 bg-gray-900/50 border border-gray-700 mt-3">
              <button 
                  onClick={() => handleModeChange('crop')}
                  className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors w-32 text-center ${mode === 'crop' ? 'bg-red-600 text-white shadow' : 'text-gray-300 hover:bg-gray-700'}`}
              >
                  자르기
              </button>
              <button 
                  onClick={() => handleModeChange('erase')}
                  className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors w-32 text-center ${mode === 'erase' ? 'bg-red-600 text-white shadow' : 'text-gray-300 hover:bg-gray-700'}`}
              >
                  글자 지우개
              </button>
            </div>
            <p className="text-gray-300 mt-2 text-sm max-w-lg">
                {mode === 'crop' 
                    ? '영역의 모서리를 드래그하여 크기를 조절하거나, 중앙을 드래그하여 이동하세요.'
                    : `지울 영역을 클릭하여 선택하세요. 선택한 영역을 다시 클릭하면 선택이 취소됩니다.`
                }
            </p>
        </div>
        <div 
          ref={containerRef}
          className={`relative ${mode === 'erase' ? 'cursor-cell' : ''} max-w-full max-h-[60vh] shadow-2xl rounded-lg overflow-hidden bg-gray-800 flex items-center justify-center`}
          {...combinedMouseEvents}
        >
            <img 
                ref={imageRef} 
                src={`data:image/png;base64,${currentImage}`} 
                alt="Editor preview" 
                className="max-w-full max-h-[60vh] object-contain block pointer-events-none" 
                draggable="false"
            />
            
            {isProcessing && (
                <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center z-30">
                    <Spinner />
                    <p className="text-white mt-2">이미지 처리 중...</p>
                </div>
            )}
            
            {mode === 'crop' && cropBox.visible && (
            <div
                className="absolute border-4 border-red-500 bg-black/30 rounded-md"
                style={{
                  left: `${imageOffset.x + cropBox.x}px`,
                  top: `${imageOffset.y + cropBox.y}px`,
                  width: `${cropBox.width}px`,
                  height: `${cropBox.height}px`,
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
                  pointerEvents: 'none'
                }}
            >
              <div 
                className="absolute inset-0 cursor-move" 
                style={{ pointerEvents: 'auto' }}
                onMouseDown={(e) => handleMouseDown(e, 'body')}
              />
              <div className="absolute -top-2 -left-2 w-4 h-4 rounded-full bg-red-500 cursor-nwse-resize" style={{ pointerEvents: 'auto' }} onMouseDown={(e) => handleMouseDown(e, 'tl')}/>
              <div className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-red-500 cursor-nesw-resize" style={{ pointerEvents: 'auto' }} onMouseDown={(e) => handleMouseDown(e, 'tr')}/>
              <div className="absolute -bottom-2 -left-2 w-4 h-4 rounded-full bg-red-500 cursor-nesw-resize" style={{ pointerEvents: 'auto' }} onMouseDown={(e) => handleMouseDown(e, 'bl')}/>
              <div className="absolute -bottom-2 -right-2 w-4 h-4 rounded-full bg-red-500 cursor-nwse-resize" style={{ pointerEvents: 'auto' }} onMouseDown={(e) => handleMouseDown(e, 'br')}/>
            </div>
            )}
            
            {mode === 'erase' && (
                <div 
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        left: imageOffset.x,
                        top: imageOffset.y,
                        width: imageRef.current?.clientWidth,
                        height: imageRef.current?.clientHeight,
                    }}
                >
                    {selectionBoxes.map((box, i) => (
                        <div key={`sel-${i}`} className="absolute border-2 border-dashed border-red-500 bg-red-500/30"
                            style={{ left: box.x, top: box.y, width: box.width, height: box.height }}
                        />
                    ))}
                    {drawingBox && (
                         <div className="absolute border-2 border-dashed border-red-500 bg-red-500/30"
                            style={{ left: drawingBox.x, top: drawingBox.y, width: drawingBox.width, height: drawingBox.height }}
                        />
                    )}
                </div>
            )}
        </div>

        {error && (
            <div className="bg-red-900/50 border border-red-700 text-red-300 p-3 rounded-lg mt-4 text-center max-w-xl">
                <p>{error}</p>
            </div>
        )}

        {mode === 'erase' && (
             <div className="mt-4 flex flex-wrap justify-center gap-3 shrink-0">
                <button onClick={handleUndo} disabled={isProcessing || historyStack.length <= 1} className="flex items-center justify-center gap-2 h-11 px-5 font-semibold rounded-md bg-gray-600 text-white transition-colors hover:enabled:bg-gray-700 disabled:opacity-50">
                    되돌리기
                </button>
                <button onClick={handleEraseAllText} disabled={isProcessing} className="flex items-center justify-center gap-2 h-11 px-5 font-semibold rounded-md bg-indigo-600 text-white transition-colors hover:enabled:bg-indigo-700 disabled:opacity-50">
                    모든 글자 지우기
                </button>
                <button onClick={handleEraseSelected} disabled={isProcessing || selectionBoxes.length === 0} className="flex items-center justify-center gap-2 h-11 px-5 font-semibold rounded-md bg-red-700 text-white transition-colors hover:enabled:bg-red-800 disabled:opacity-50">
                    선택 영역 지우기
                </button>
                 <button onClick={() => setSelectionBoxes([])} disabled={isProcessing || selectionBoxes.length === 0} className="flex items-center justify-center gap-2 h-11 px-5 font-semibold rounded-md bg-yellow-600 text-white transition-colors hover:enabled:bg-yellow-700 disabled:opacity-50">
                    선택 초기화
                </button>
             </div>
        )}

       <div className="mt-6 flex gap-4 shrink-0">
          <button onClick={onClose} className="flex items-center justify-center h-12 px-6 font-semibold rounded-md bg-gray-700 text-white transition-all duration-300 ease-in-out hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500">
            취소
          </button>
          <button onClick={() => onSave(currentImage)} className="flex items-center justify-center h-12 px-6 font-semibold rounded-md bg-amber-600 text-white transition-all duration-300 ease-in-out hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500">
            크롭하지 않음
          </button>
           <button onClick={performCrop} className="flex items-center justify-center h-12 px-6 font-semibold rounded-md bg-red-600 text-white transition-all duration-300 ease-in-out hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500">
            완료 및 확인
          </button>
      </div>
      <style>{`
        @keyframes fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
        }
        .animate-fade-in {
            animation: fade-in 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default ImageEditor;